import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs';
import { EventNames, VgimAdobeLaunchService } from 'vg-interaction-model';

import { LogInfo } from '../../actions';
import {
  AdobeActionTypes,
  CallToActionData,
  FileUploadData,
  ProcessAbandonData,
  ProcessCompleteData,
  ProcessErrorData,
  ProcessSideStepData,
  ProcessStartData,
  ProcessStepData,
} from '../../actions/adobe-launch/adobe-launch.actions';

@Injectable()
export class AdobeLaunchEffects {
  constructor(
    private actions$: Actions,
    private adobeLaunchService: VgimAdobeLaunchService,
  ) {}

  fireProcessStepEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessStep),
      map((action: ProcessStepData) => {
        const data = {
          processType: action.processType,
          processStep: action.processStep,
          ...(action.accountType && { accountType: action.accountType }),
        };
        this.adobeLaunchService.push({ event: EventNames.PROCESS_STEP_SUCCESS, process: data });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireProcessStartEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessStart),
      map((action: ProcessStartData) => {
        const data = {
          processType: action.processType,
          ...(action.processTrackingId && { processTrackingId: action.processTrackingId }),
          ...(action.accountType && { accountType: action.accountType }),
          ...(action.tickerSymbol && { tickerSymbol: action.tickerSymbol }),
        };
        this.adobeLaunchService.push({ event: EventNames.PROCESS_START, process: data });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireCallToActionEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackCallToAction),
      map((action: CallToActionData) => {
        const data = {
          location: action.location,
          ctaName: action.ctaName,
          ctaType: action.ctaType,
        };

        this.adobeLaunchService.push({
          event: EventNames.CALL_TO_ACTION,
          callToAction: data,
          fireTrackLink: {
            fireTrackLink: 'true',
          },
        });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireProcessAbandonEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessAbandon),
      map((action: ProcessAbandonData) => {
        const data = {
          processStep: action.processStep,
          processType: action.processType,
          processAbandonmentType: action.processAbandonmentType,
        };
        this.adobeLaunchService.push({ event: EventNames.PROCESS_ABANDONED, process: data });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireProcessSideStepEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessSideStep),
      map((action: ProcessSideStepData) => {
        const data = {
          processType: action.processType,
          processSideStep: action.processSideStep,
        };
        this.adobeLaunchService.push({
          event: EventNames.PROCESS_SIDE_STEP_SUCCESS,
          process: data,
        });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireProcessCompleteEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessComplete),
      map((action: ProcessCompleteData) => {
        const data = {
          processType: action.processType,
          ...(action.transactionId && { transactionId: action.transactionId }),
          ...(action.accountType && { accountType: action.accountType }),
          ...(action.amountTransacted && { amountTransacted: action.amountTransacted }),
        };
        this.adobeLaunchService.push({ event: EventNames.PROCESS_COMPLETE, process: data });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireFileUploadEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackFileUpload),
      map((action: FileUploadData) => {
        const data = {
          documentType: action.documentType,
        };
        this.adobeLaunchService.push({ event: EventNames.FILE_UPLOAD_SUCCESSFUL, linkInfo: data });
        return LogInfo({ payload: data });
      }),
    ),
  );

  fireSendDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackSendDocuments),
      map(() => {
        this.adobeLaunchService.push({ event: EventNames.SEND_DOCUMENTS });
        return LogInfo({ payload: { message: 'File(s) uploaded to Appian' } });
      }),
    ),
  );

  fireProcessErrorEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdobeActionTypes.TrackProcessError),
      map((action: ProcessErrorData) => {
        const data = {
          errorCode: action.errorCode,
        };
        this.adobeLaunchService.push({ event: EventNames.PROCESS_ERROR, process: data });
        return LogInfo({ payload: data });
      }),
    ),
  );
}
